/* This component should subtitute DatePicker once DEV-95888 is done */
import React, {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import DayPicker from 'react-day-picker/DayPicker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import { t } from '@lingui/core/macro';
import moment from 'moment';
import styled from 'styled-components';

import ScrollableDayPicker from '@rover/components/src/datetime/ScrollableDayPicker/ScrollableDayPicker';
import FormBasicValidationError from '@rover/components/src/formFields/FormValidationError/FormBasicValidationError';
import InlineErrorDateInput, {
  errorMessageType,
} from '@rover/components/src/formFields/InlineErrorDateInput/InlineErrorDateInputGeneric';
import Calendar from '@rover/icons/svg/calendar.svg';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import LabelAndErrorFormGroup from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import useOnClickOutside from '@rover/react-lib/src/hooks/useOnClickOutside';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import type { Day } from '@rover/types/src/datetime/Day';
import Enum from '@rover/types/src/Enum';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';

import '@rover/utilities/allMomentLocales';
import 'react-day-picker/lib/style.css';

import { DatePickerProps } from '../GenericCalendar';
import MultiDatePickerGeneric from '../MultiDatePicker/MultiDatePickerGeneric';

import createRenderDayForHolidays from './createRenderDayForHolidays';
import DatePickerCaption from './DatePickerCaption';
import DatePickerNavbar from './DatePickerNavbar';
import DatePickerStylingWrapper from './DatePickerStylingWrapper';

const DayPickerLazy = React.lazy(() => import('react-day-picker'));

export const StyledIcon = styled(Calendar)<{ iconStyle?: React.CSSProperties }>`
  position: absolute;
  height: ${({ iconStyle, theme }) => iconStyle?.height ?? theme.sizes['4x']};
  width: ${({ iconStyle, theme }) => iconStyle?.width ?? theme.sizes['4x']};
  right: ${({ iconStyle, theme }) => iconStyle?.right ?? theme.sizes['4x']};
  top: ${({ theme }) => theme.sizes['3x']};
  fill: ${({ theme }): string => theme.textColor.tertiary};
  pointer-events: none;
`;

export const StyledCloseIcon = styled(Close)<{ iconStyle?: React.CSSProperties }>`
  position: absolute;
  height: ${({ iconStyle, theme }) => iconStyle?.height ?? theme.sizes['6x']};
  width: ${({ iconStyle, theme }) => iconStyle?.width ?? theme.sizes['6x']};
  right: ${({ iconStyle, theme }) => iconStyle?.right ?? theme.sizes['0x']};
  fill: ${({ theme }): string => theme.textColor.tertiary};
  pointer-events: auto;
`;

const StyledDayPicker = styled(DayPickerLazy)`
  ${MQ.XXS.toString()} {
    .DayPicker-Day {
      aspect-ratio: 1 / 1; //Keeps a square shape for the day
      box-sizing: border-box;
      min-width: unset;
      width: calc(100% / 7);
    }
  }
`;

export class CalendarDirection extends Enum<string> {
  static UP = new CalendarDirection('up');

  static DOWN = new CalendarDirection('down');

  static FLEX = new CalendarDirection('flex');

  static isLocked = true;
}

export function parseDatePopoverErrors(validationErrors, isInExperiment): undefined | string {
  /* The component receives errors from MultiDateTimePicker, which contain both date and time errors.
  To show the correct message on the calendar, we need to distinguish between them: one error = date validation,
  list of errors = time picker's validation. In the future, popovers will be deprecated and date/time components
  separated - remember to get rid of this function if newCalendarInteraction experiment wins! */
  if (isInExperiment) {
    return undefined;
  }
  return Array.isArray(validationErrors) ? undefined : validationErrors;
}

type DayPickerInputProps = React.ComponentProps<typeof DayPickerInput>;

const DATE_PICKER_CALENDAR_HEIGHT = 320;

export type DatePickerHandle = {
  showDatePicker: () => void;
};

type KeyboardEvent = {
  key: string;
  preventDefault(): void;
};

function checkDateErrors(selectedDate, disableValidation = false): string | undefined {
  if (disableValidation) return undefined;

  if (!selectedDate) {
    return t`Select start date`;
  }
  return undefined;
}

const DatePicker = forwardRef<DatePickerHandle, DatePickerProps>(function DatePicker(
  {
    allowKeyboardInput = true,
    asCalendar = false,
    className = '',
    date,
    id = '',
    isDayBlocked,
    language,
    minDate = new Date(),
    maxDate,
    initialMonth,
    onChange,
    placeholder = '',
    calendarDirection = CalendarDirection.FLEX,
    holidays = [],
    modifiers,
    validationError: validationErrorProp,
    validationType,
    newCalendarInteraction = false,
    serviceName = '',
    serviceSubtitle = '',
    disableValidation = false,
    isMobileBrowser = false,
    inputStyle,
    onClickFromSearch,
    isDismissible,
    isRequired,
    syncInputWithDate = false,
    overrideTitle,
    overrideSubtitle,
    hideCloseButton,
    showInputClearButton,
    ...other
  }: DatePickerProps,
  externalDatepickerRef
) {
  let focusTimeout: ReturnType<typeof setTimeout> | undefined;
  let blurTimeout: ReturnType<typeof setTimeout> | undefined;
  let observer: IntersectionObserver | undefined;
  let potentialValidationError: errorMessageType;
  let isObserving = false;

  const { sizes, borderWidth, radii, colors } = useTheme();
  const [userIsTouching, setUserIsTouching] = useState(false);
  const [validationError, setValidationError] = useState<errorMessageType>(undefined);
  const [bottomSheetValidationError, setBottomSheetValidationError] =
    useState<errorMessageType>(undefined);
  const [calendarIsOpen, setCalendarIsOpen] = useState(false);
  const [calendarDropUp, setCalendarDropUp] = useState(false);
  const [calendarHeightOffset, setCalendarHeightOffset] = useState(40);
  const [calendarInset, setCalendarInset] = useState(0);
  const [touchingWhileOpen, setTouchingWhileOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState<boolean | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(date || null);

  const { DF } = useI18nContext();

  const calendarWrapperId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dayPickerRef = useRef<DayPickerInput>(null);

  useOnClickOutside(wrapperRef, () => {
    setShowCalendar(false);
    if (showCalendar) {
      if (!disableValidation) setValidationError(checkDateErrors(selectedDate));
    }
  });

  const inputPassedRootMargin = (entries: IntersectionObserverEntry[]): void => {
    if (!isObserving) return; // This prevents the function from executing if it's still in the JS event loop queue when unobserve is called

    entries.forEach((entry) => {
      setCalendarDropUp(
        calendarDirection !== CalendarDirection.DOWN && entry.intersectionRatio < 1
      );
    });
  };

  const getObserver = (): IntersectionObserver =>
    new IntersectionObserver(inputPassedRootMargin, {
      root: null,
      rootMargin: `0px 0px -${DATE_PICKER_CALENDAR_HEIGHT}px 0px`,
      threshold: 1.0,
    });

  const observe = (): void => {
    isObserving = true;
    observer = getObserver();
    if (!(wrapperRef.current instanceof HTMLElement)) return;
    observer.observe(wrapperRef.current);
    const inputHeight = wrapperRef.current.getBoundingClientRect().height;
    setCalendarHeightOffset(inputHeight);
  };

  const unobserve = (): void => {
    if (observer && wrapperRef?.current) {
      observer.unobserve(wrapperRef.current);
      isObserving = false;
    }
  };

  useEffect(() => {
    return (): void => {
      unobserve();
      clearTimeout(focusTimeout);
      clearTimeout(blurTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedDate) {
      setBottomSheetValidationError(undefined);
    }
  }, [serviceName, selectedDate]);

  useEffect(() => {
    if (syncInputWithDate) {
      setSelectedDate(date ?? null);
    }
  }, [date, syncInputWithDate]);

  const dayPickerLateralAdjust = (): void => {
    const windowWidth = window.innerWidth;
    const inputEl = wrapperRef?.current;
    const wrapperEl = inputEl?.getElementsByClassName('DayPickerInput-Overlay')[0];
    if (!inputEl || !wrapperEl) return;
    const { width: calendarWidth } = wrapperEl.getBoundingClientRect();
    const { left: inputLeft, right: inputRight } = inputEl.getBoundingClientRect();
    const calendarRight = inputLeft + calendarWidth;
    let inset = 0;

    // If the calendar goes off the right side of the screen, try the left instead
    if (calendarRight >= windowWidth) {
      const overshootRight = calendarRight - windowWidth;
      inset = calendarRight - inputRight;
      const overshootLeft = inset - inputLeft;

      // If the calendar overshoots the left more than the right, reset to right
      if (overshootLeft > overshootRight) {
        inset = 0;
      }
    }

    // Make sure the calendar is inside the right side of the screen
    if (calendarRight - inset > windowWidth) {
      inset = calendarRight - windowWidth;
    }

    // Make sure the calendar is inside the left side of the screen
    if (inputLeft - inset < 0) {
      inset = inputLeft;
    }

    setCalendarInset(inset);
  };

  const handleDayChange: DayPickerInputProps['onDayChange'] = (newDate: Date, { disabled }) => {
    if (disabled) return;

    if (newDate) {
      potentialValidationError = undefined;
      setValidationError(undefined);
      onChange(newDate);
      setSelectedDate(newDate);
      !isMobileBrowser && setShowCalendar(false);
    } else {
      potentialValidationError = `Use the format ${
        getDateTimeFormatMapForLang(language).DATE_SHORT
      }`;
    }
  };

  const handleOutsideTouchMove = (): void => setTouchingWhileOpen(true);
  const handleOutsideTouchEnd = useCallback(
    (event: Event): void => {
      // Close if there was no scrolling
      if (!touchingWhileOpen) {
        // `dayPickerRef.getDayPicker().dayPicker` has the DOM element
        const dayPickerComponent = dayPickerRef?.current?.getDayPicker();

        if (dayPickerComponent) {
          const dayPickerEl = dayPickerComponent.dayPicker;
          const dayPickerInputEl = dayPickerRef?.current?.getInput();

          const targetIsOutsideDayPicker =
            !dayPickerEl.contains(event.target as Node) &&
            !dayPickerInputEl.contains(event.target as Node);

          if (targetIsOutsideDayPicker && dayPickerRef?.current) {
            dayPickerRef.current.hideDayPicker();
          }
        }
      }

      setTouchingWhileOpen(false);
    },
    [touchingWhileOpen]
  );

  useEffect(() => {
    if (calendarIsOpen) {
      document.addEventListener('touchend', handleOutsideTouchEnd, false);
    }
    return (): void => {
      document.removeEventListener('touchend', handleOutsideTouchEnd, false);
    };
  }, [calendarIsOpen, handleOutsideTouchEnd]);

  useEffect(() => {
    if (showCalendar && onClickFromSearch) onClickFromSearch();
    if (showCalendar === false && newCalendarInteraction && isMobileBrowser) {
      const error = checkDateErrors(selectedDate);
      if (!disableValidation) setValidationError(error);
    }
  }, [showCalendar, newCalendarInteraction, isMobileBrowser, onClickFromSearch, disableValidation]);

  const handleDayPickerShow = (): void => {
    dayPickerLateralAdjust();
    // Add a slight delay to allow the IntersectionObserver to update before we display the dropdown
    focusTimeout = setTimeout(() => setCalendarIsOpen(true), 100);
    observe();
    document.addEventListener('touchmove', handleOutsideTouchMove, false);
  };

  const handleDayPickerHide = (): void => {
    unobserve();
    setCalendarIsOpen(false);
    document.removeEventListener('touchmove', handleOutsideTouchMove, false);
  };

  const handleInputBlur = (): void => {
    // setTimeout here ensures this.state.calendarIsOpen below accurately reflects the UI
    setTimeout(() => {
      // avoid inadvertently closing the calendar UI by only updating state when the calendar is closed
      if (potentialValidationError && !calendarIsOpen && !disableValidation) {
        setValidationError(potentialValidationError);
      }
    }, 0);
  };

  // last day in the array is the one the user selected
  const handleCalendarChange = (selectedDays: Day[]): void => {
    const addedDay = selectedDays.pop();
    if (addedDay) onChange(addedDay.date ? addedDay.date : undefined);
  };

  const handleTouchStart = (): void => {
    // `userIsTouching` temporarily sets input to readonly to avoid popping keyboard on mobile
    setUserIsTouching(true);
  };

  const showDatePicker = (): void => {
    if (dayPickerRef?.current) {
      dayPickerRef.current.getInput().focus();
      observe();
      dayPickerRef.current.showDayPicker();
      setTimeout(() => dayPickerLateralAdjust());
    }
  };

  useImperativeHandle(externalDatepickerRef, () => ({ showDatePicker }));

  const dayPickerProps = {
    value: date,
    fromMonth: minDate,
    toMonth: maxDate,
    initialMonth,
    month: isMobileBrowser ? minDate : initialMonth || minDate,
    disabledDays: [
      {
        before: minDate,
        after: maxDate,
      },
      isDayBlocked,
    ],
    selectedDays: selectedDate ? [selectedDate] : [],
    navbarElement: DatePickerNavbar,
    captionElement: DatePickerCaption,
    localeUtils: MomentLocaleUtils,
    locale: language,
    modifiers: {
      holiday: holidays,
      ...modifiers,
    },
    renderDay: createRenderDayForHolidays(holidays || []),
  };

  const readOnly = allowKeyboardInput ? userIsTouching : true;

  function getStringFromDates(): string {
    return selectedDate ? moment(selectedDate).format('ddd, DD MMM YYYY') : '';
  }

  const title = overrideTitle ?? (serviceName as string);
  const subtitle = useMemo(() => {
    if (!selectedDate) {
      return overrideSubtitle ?? t`Select start date`;
    }
    return undefined;
  }, [selectedDate, overrideSubtitle]);

  function clearDateSelection(): void {
    setSelectedDate(null);
    onChange(undefined);
  }

  const numberOfMonthsAllowed = (): number => {
    if (maxDate) {
      // Adding 2 to the result to count both first and last month
      return Math.abs(moment().diff(moment(maxDate), 'months')) + 2;
    }
    // Adding 2 to the result to count both first and last month
    return Math.abs(moment().diff(moment(getMaxRequestDate()), 'months')) + 2;
  };

  const ariaAnnouncement = date ? t`Selected date ${moment(date).format(DF.DATE_FULL)}` : '';

  const calendarIcon = <Calendar width="24px" height="24px" fill={colors.text.tertiary} />;

  const renderInput = (isMobile = false, isReadOnly = false): React.ReactNode => {
    return (
      <Box
        width={isMobileBrowser && showCalendar ? '100%' : 'unset'}
        position="relative"
        marginBottom="4x"
      >
        <InlineErrorDateInput
          inputId={id}
          style={inputStyle}
          className={className}
          onKeyDown={(event: KeyboardEvent): void => {
            if (event.key === 'Enter') {
              setShowCalendar(!showCalendar);
            } else if (event.key === ' ') {
              event.preventDefault();
              setShowCalendar(!showCalendar);
            }
          }}
          onClick={(): void => {
            !isMobile && setShowCalendar(!showCalendar);
          }}
          value={getStringFromDates()}
          errorMessage={validationError || validationErrorProp}
          placeholder={placeholder}
          onIconClick={(): void => {
            !isMobile && setShowCalendar(!showCalendar);
          }}
          aria-expanded={showCalendar}
          aria-controls={calendarWrapperId}
          aria-labelledby={other['aria-labelledby']}
          aria-label={other['aria-label']}
          aria-required={isRequired}
          tabIndex={isReadOnly ? -1 : 0}
          icon={
            showInputClearButton ? (
              <StyledCloseIcon onClick={() => clearDateSelection()} />
            ) : (
              calendarIcon
            )
          }
        />
      </Box>
    );
  };

  const renderMobile = (formGroupProps): JSX.Element => {
    return (
      <BottomSheet
        isOpen={!!showCalendar}
        onRequestClose={() => setShowCalendar(false)}
        isDismissible={isDismissible}
        hideCloseIcon={hideCloseButton}
      >
        <DatePickerStylingWrapper
          id={calendarWrapperId}
          className={className}
          readOnly={readOnly}
          styleAsError={!!validationError || !!validationErrorProp}
          calendarDropUp={calendarDirection === CalendarDirection.UP || calendarDropUp}
          ref={isMobileBrowser ? null : wrapperRef}
          calendarHeightOffset={calendarHeightOffset}
          calendarInset={calendarInset}
          calendarIsOpen={calendarIsOpen}
          newCalendarInteraction={newCalendarInteraction}
          isMobileBrowser={newCalendarInteraction && isMobileBrowser}
        >
          <Flex flexDirection="column" alignItems="center">
            <A11yHiddenBox aria-live="assertive" role="alert">
              {ariaAnnouncement}
            </A11yHiddenBox>
            <Heading size="200" mb="1x">
              {title}
            </Heading>
            <Paragraph size="100" mb="6x" textAlign="center" textColor="secondary">
              {subtitle}
            </Paragraph>
            <ScrollableDayPicker
              wrapperStyles={{ overflow: 'auto', height: '350px', width: '100%' }}
              initialMonth={dayPickerProps.initialMonth || dayPickerProps.value}
            >
              <Suspense fallback={null}>
                <StyledDayPicker
                  {...formGroupProps}
                  {...dayPickerProps}
                  initialMonth={undefined}
                  onDayClick={handleDayChange}
                  ref={dayPickerRef}
                  canChangeMonth={false}
                  numberOfMonths={numberOfMonthsAllowed()}
                />
              </Suspense>
            </ScrollableDayPicker>
            {bottomSheetValidationError && (
              <Flex marginLeft="0x" width="100%">
                <FormBasicValidationError
                  errorMessage={checkDateErrors(selectedDate) as string}
                  id="input-error"
                />
              </Flex>
            )}
            <Button
              data-testid="button-save-date-generic-mobile"
              mt="6x"
              size="small"
              variant="primary"
              fullWidth
              sx={{
                p: '0x',
                height: sizes['8x'],
              }}
              onClick={(): void => {
                const error = checkDateErrors(selectedDate);
                if (!disableValidation) setValidationError(error);
                setBottomSheetValidationError(error);
                if (!error) {
                  setShowCalendar(false);
                }
              }}
            >
              {t`Save date`}
            </Button>
            <Button
              data-testid="button-clear-date-generic-mobile"
              mt="3x"
              size="small"
              variant="flat"
              fullWidth
              sx={{
                height: sizes['8x'],
                p: '0x',
              }}
              onClick={(): void => {
                if (selectedDate) {
                  setBottomSheetValidationError(undefined);
                }
                clearDateSelection();
                const error = checkDateErrors(undefined);
                if (!disableValidation) {
                  setValidationError(error);
                }
              }}
            >
              {t`Clear date`}
            </Button>
          </Flex>
        </DatePickerStylingWrapper>
      </BottomSheet>
    );
  };

  const renderDesktop = (formGroupProps): JSX.Element => {
    return (
      <Box
        id={calendarWrapperId}
        pb="0x"
        mt="5x"
        sx={{ backgroundColor: 'white' }}
        width="640px"
        border={`${borderWidth.primary} solid ${colors.border.primary}`}
        borderRadius={radii.secondary}
        data-testid="date-picker-desktop"
      >
        <DayPicker
          {...formGroupProps}
          {...dayPickerProps}
          onDayClick={handleDayChange}
          ref={dayPickerRef}
          numberOfMonths={2}
        />
      </Box>
    );
  };

  const dayPickerInputProps = {
    value: date,
    onDayChange: handleDayChange,
    onDayPickerShow: handleDayPickerShow,
    onDayPickerHide: handleDayPickerHide,
    format: getDateTimeFormatMapForLang(language).DATE_SHORT,
    formatDate: MomentLocaleUtils.formatDate,
    parseDate: MomentLocaleUtils.parseDate,
    dayPickerProps,
    inputProps: {
      autoComplete: 'off',
      id,
      onBlur: handleInputBlur,
      onTouchStart: handleTouchStart,
      readOnly,
    },
  };

  if (asCalendar) {
    const selectedDays = date ? [dayFactory({ date })] : [];
    return (
      <MultiDatePickerGeneric
        isMobileBrowser={!!isMobileBrowser}
        {...other}
        {...dayPickerProps}
        selectedDays={selectedDays}
        onChange={handleCalendarChange}
        disableBeforeDateTime={minDate}
        disableAfterDateTime={maxDate || undefined}
        holidays={holidays}
        language={language}
        validationError={validationErrorProp}
        validationType={validationType}
        serviceSubtitle={serviceSubtitle}
        serviceName={serviceName}
        newCalendarInteraction={newCalendarInteraction}
      />
    );
  }

  return (
    <LabelAndErrorFormGroup
      {...other}
      useLabelElementAsCaption={false}
      validationError={parseDatePopoverErrors(
        validationError || validationErrorProp,
        newCalendarInteraction
      )}
      validationType={validationType}
    >
      {({ ...formGroupProps }): JSX.Element => {
        // In some cases, we need the `id` attribute generated from the `LabelAndErrorFormGroup`
        dayPickerInputProps.inputProps.id = dayPickerInputProps.inputProps.id || formGroupProps.id;

        return (
          <DatePickerStylingWrapper
            className={className}
            readOnly={readOnly}
            styleAsError={!!validationError || !!validationErrorProp}
            calendarDropUp={calendarDirection === CalendarDirection.UP || calendarDropUp}
            ref={isMobileBrowser ? null : wrapperRef}
            calendarHeightOffset={calendarHeightOffset}
            calendarInset={calendarInset}
            calendarIsOpen={calendarIsOpen}
            newCalendarInteraction={newCalendarInteraction}
            isMobileBrowser={newCalendarInteraction && isMobileBrowser}
            data-testid="date-picker-generic"
          >
            {newCalendarInteraction && renderInput()}
            {newCalendarInteraction &&
              showCalendar &&
              isMobileBrowser &&
              renderMobile(formGroupProps)}
            {newCalendarInteraction &&
              showCalendar &&
              !isMobileBrowser &&
              renderDesktop(formGroupProps)}
            {!newCalendarInteraction && (
              <DayPickerInput
                {...formGroupProps}
                {...dayPickerInputProps}
                placeholder={placeholder}
                inputProps={{
                  ...dayPickerInputProps.inputProps,
                  id,
                  // This label helps screen readers tell users about the date format they must type
                  // It gets added here, so we have access to i18n
                  'aria-label': t({
                    message: `date input in ${
                      getDateTimeFormatMapForLang(language).DATE_SHORT
                    } format`,
                    comment:
                      "The var here will be a localized date string format, ex: 'date input in MM/DD/YYYY format'",
                  }),
                }}
                ref={dayPickerRef}
              />
            )}
            {!newCalendarInteraction && <StyledIcon onClick={showDatePicker} />}
          </DatePickerStylingWrapper>
        );
      }}
    </LabelAndErrorFormGroup>
  );
});

export default DatePicker;
